"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Section from "./section";
import Link from "next/link";
import CategoryCard from "./category-card";
import categoriesData from "@/public/categories.json";
import { motion } from "motion/react";

interface Category {
  readonly key: string;
  readonly image: string;
  readonly description: string;
}

const categories = categoriesData as readonly Category[];

const FEATURED_CATEGORIES = [
  "ROTORS & STATORS",
  "SPRAY PLASTER MACHINE",
  "CONVEYOR SYSTEM AND COMPRESSOR",
] as const;

export default function ProductShowcaseSection() {
  const featuredCategories = categories.filter((category) =>
    FEATURED_CATEGORIES.includes(category.key as typeof FEATURED_CATEGORIES[number])
  );

  return (
    <Section className="mt-48 mb-24 overflow-x-clip">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
        className="mb-8 md:mb-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
      >
        <div className="space-y-4">
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            Featured
          </motion.span>
          <h2 className="text-4xl font-bold text-foreground md:text-5xl lg:text-6xl">
            Top Products
          </h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="h-1 w-20 bg-linear-to-r from-primary to-secondary rounded-full origin-left"
          />
        </div>
        <Button
          size="lg"
          asChild
          className="group"
        >
          <Link href="/categories" title="Show all">
            Show all
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Button>
      </motion.div>

      {/* Categories Grid */}
      <div className="grid gap-6 md:grid-cols-3 lg:gap-8">
        {featuredCategories.map((category, index) => (
          <motion.div
            key={category.key}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <CategoryCard
              name={category.key}
              category={category.key}
              imageUrl={category.image}
              imageSize="large"
              description={category.description}
            />
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
